// src/pages/dashboard/MySkills/MySkills.jsx
import "../../styles/myskills.css";
import useMySkills from "./useMySkills";

export default function MySkills() {
  const {
    loading,
    error,
    analysis,
    periodAnalysis,
    lastUpdated,
    period,
    setPeriod,
    selectedDate,
    setSelectedDate,
    todaySkills,
    levelLabel,
    refreshAll,
    loadPeriodAnalysis,
  } = useMySkills();

  const renderSource = period === "all" ? analysis : periodAnalysis?.analysis || periodAnalysis;

  const normalized = renderSource || {};

  const buildSkills = () => {
    const all = normalized.all_skills || {};
    const levels = normalized.skill_levels || {};
    const trend = normalized.trend || {};

    return Object.keys(all)
      .map((k) => ({
        name: k,
        count: all[k],
        level: levelLabel(levels[k]),
        trend:
          trend.improving?.includes(k)
            ? "rising"
            : trend.declining?.includes(k)
            ? "falling"
            : "stable",
      }))
      .sort((a, b) => b.count - a.count);
  };

  const skills = buildSkills();
  const categories = normalized.categories || {};
  const topSkills = normalized.top_skills || normalized.top_3_skills || [];
  const insights = normalized.insights || [];
  const skillsToImprove = normalized.skills_to_improve || [];

  const topClass = (i) =>
    i === 0 ? "trophy gold" : i === 1 ? "trophy silver" : i === 2 ? "trophy bronze" : "trophy";

  const maxCount = Math.max(...skills.map((s) => s.count), 1);

  if (loading) return <div className="ms-loading">Analyzing…</div>;
  if (error) return <div className="ms-error">{error}</div>;

  return (
    <div className="ms-wrapper premium">
      {/* Header */}
      <div className="ms-header-row">
        <div>
          <h1 className="ms-title">My Skills</h1>
          <p className="ms-subtitle">Backend-powered analysis • Skill insights</p>
        </div>

        <div className="ms-controls">
          <select
            className="ms-period-select"
            value={period}
            onChange={(e) => {
              const p = e.target.value;
              setPeriod(p);

              const today = new Date();
              const year = today.getFullYear();

              if (p === "weekly") {
                const firstJan = new Date(year, 0, 1);
                const week = String(
                  Math.ceil(
                    ((today - firstJan) / 86400000 + firstJan.getDay() + 1) / 7
                  )
                ).padStart(2, "0");
                setSelectedDate(`${year}-W${week}`);
              } else if (p === "monthly") {
                setSelectedDate(`${year}-${String(today.getMonth() + 1).padStart(2, "0")}`);
              } else {
                setSelectedDate(today.toISOString().split("T")[0]);
              }
            }}
          >
            <option value="all">All periods</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>

          <input
            className="ms-date-input"
            type={period === "weekly" ? "week" : period === "monthly" ? "month" : "date"}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />

          <button className="ms-refresh" onClick={loadPeriodAnalysis}>
            🔎 Analyze
          </button>

          <button className="ms-refresh ms-light" onClick={refreshAll}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Today's Skills */}
      {todaySkills.length > 0 && (
        <div className="ms-today-box">
          <h3 className="section-title">Recent Skills (Latest Reflection)</h3>
          <div className="ms-today-skill-list">
            {todaySkills.map((s, i) => (
              <span key={i} className="ms-today-skill-item">{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Top 3 Trophies */}
      <div className="ms-trophies-row">
        {topSkills.slice(0, 3).map((s, i) => (
          <div key={i} className={topClass(i)}>
            <div className="trophy-rank">#{i + 1}</div>
            <div className="trophy-skill">{s}</div>
            <div className="trophy-meta">
              <div className="trophy-count">{normalized.all_skills?.[s] || 0} mentions</div>
              <div className="trophy-badge">{i === 0 ? "Gold" : i === 1 ? "Silver" : "Bronze"}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Insights */}
      <div className="ms-insights-row">
        <div className="ms-insight">
          <div className="insight-title">Top skills</div>
          <div className="insight-value">{topSkills.length ? topSkills.join(", ") : "-"}</div>
        </div>

        <div className="ms-insight">
          <div className="insight-title">Skills to improve</div>
          <div className="insight-value">
            {skillsToImprove.length ? skillsToImprove.join(", ") : "-"}
          </div>
        </div>

        <div className="ms-insight">
          <div className="insight-title">Total skills</div>
          <div className="insight-value">{Object.keys(normalized.all_skills || {}).length}</div>
        </div>

        <div className="ms-insight">
          <div className="insight-title">Total mentions</div>
          <div className="insight-value">
            {Object.values(normalized.all_skills || {}).reduce((a, b) => a + b, 0)}
          </div>
        </div>

        <div className="ms-insight small">
          <div className="insight-title">Last updated</div>
          <div className="insight-value small">
            {lastUpdated ? new Date(lastUpdated).toLocaleString() : "-"}
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="ms-main-grid">
        {/* Left: Categories */}
        <div className="ms-left-col">
          <h3 className="section-title">Category Breakdown</h3>

          {Object.keys(categories).length === 0 && (
            <div className="ms-empty">No category data</div>
          )}

          {Object.entries(categories).map(([cat, list]) => (
            <div key={cat} className="cat-block">
              <div className="cat-header">
                <div className="cat-name">{cat}</div>
                <div className="cat-count">
                  {list.reduce(
                    (sum, skill) => sum + (normalized.all_skills?.[skill] || 0),
                    0
                  )}{" "}
                  mentions
                </div>
              </div>

              <div className="cat-items">
                {list.map((k) => (
                  <div key={k} className="cat-item">
                    <div className="cat-item-left">
                      <div className="cat-item-skill">{k}</div>
                      <div className="cat-item-count">{normalized.all_skills?.[k] || 0}x</div>
                    </div>

                    <div className="cat-item-right">
                      <div className="cat-item-level">
                        {levelLabel(normalized.skill_levels?.[k])}
                      </div>

                      <div
                        className={`cat-item-trend ${
                          normalized.trend?.improving?.includes(k)
                            ? "rising"
                            : normalized.trend?.declining?.includes(k)
                            ? "falling"
                            : "stable"
                        }`}
                      >
                        {normalized.trend?.improving?.includes(k)
                          ? "↑"
                          : normalized.trend?.declining?.includes(k)
                          ? "↓"
                          : "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Right: All Skills */}
        <div className="ms-right-col">
          <h3 className="section-title">All Skills</h3>

          <div className="ms-grid-advanced">
            {skills.length === 0 && <div className="ms-empty">No skills found</div>}

            {skills.map((s) => (
              <div key={s.name} className="ms-card-adv">
                <div className="ms-card-adv-header">
                  <div className="ms-skill-adv">{s.name}</div>
                  <div className="ms-meta-adv">
                    <span className="ms-count-adv">{s.count}x</span>
                    <span className="ms-level-adv">{s.level}</span>
                  </div>
                </div>

                <div className="ms-progress-adv">
                  <div className="ms-progress-bar-adv">
                    <div
                      className="ms-progress-fill-adv"
                      style={{
                        width: `${Math.round((s.count / maxCount) * 100)}%`,
                      }}
                    ></div>
                  </div>

                  <div className={`ms-trend-badge ${s.trend}`}>
                    {s.trend.charAt(0).toUpperCase() + s.trend.slice(1)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Insights */}
      <div className="ms-footer-note">
        <strong>Insights</strong>
        <ul>
          {insights.length ? insights.map((it, i) => <li key={i}>{it}</li>) : <li>No insights</li>}
        </ul>
      </div>

      <button className="ms-refresh-fixed" onClick={refreshAll}>
        🔄 Refresh
      </button>
    </div>
  );
}